import React from "react";
import type { DefaultValues } from "react-hook-form";
import { useForm as useRHForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z } from "zod";
import { toast } from "sonner";

export const hasError = <_T extends Record<string, unknown>>(formState: {
  errors: Record<string, unknown>;
}): boolean => {
  const hasErrors = Object.keys(formState.errors).length > 0;
  if (hasErrors) {
    console.warn("Form errors", formState.errors);
  }
  return hasErrors;
};

// Basic form hook wrapper around React Hook Form
export function useForm<T extends Record<string, unknown>>(
  options: {
    schema?: z.ZodSchema<T>;
    defaultValues?: DefaultValues<T>;
    onSubmit?: (
      data: T,
    ) =>
      | Promise<{ success: boolean; message?: string }>
      | { success: boolean; message?: string };
    resetOnSuccess?: boolean;
  } = {},
) {
  const { schema, defaultValues, onSubmit, resetOnSuccess = true } = options;

  const formMethods = useRHForm<T>({
    resolver: schema ? zodResolver(schema) : undefined,
    defaultValues,
  });

  const { handleSubmit, reset, formState } = formMethods;

  const handleFormSubmit = handleSubmit(async (data) => {
    if (!onSubmit) return;

    try {
      const response = await onSubmit(data);

      if (response.success) {
        toast.success(response.message ?? "Success!");
        if (resetOnSuccess) {
          reset(defaultValues as T);
        }
      } else {
        console.error("Form error:", response);
        toast.error(response.message ?? "Something went wrong");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error("Something went wrong");
    }
  });

  return {
    ...formMethods,
    handleFormSubmit,
    hasError: () => hasError({ errors: formState.errors }),
  };
}
